import { computed, inject } from '@angular/core';

import { Observable, of, pipe } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { Translation, TranslocoService } from '@jsverse/transloco';

import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { withReset } from '@nbx/core/state';

import { LanguageService } from '../language.service';

export interface MenuItem {
  label: string;
  value: string;
}

interface TranslationStateModel {
  activeLanguage: string;
  availableLanguages: string[];
  loading: boolean;
}

/**
 * Signal store facade over Transloco + LanguageService: active/available languages as state,
 * synchronous/streaming translation helpers as methods, and a per-language cache of API-loaded
 * translation scopes. Language is device-level state, so it is excluded from the global
 * logout reset (`withReset({ global: false })`).
 */
export const TranslationStore = signalStore(
  { providedIn: 'root' },
  withState<TranslationStateModel>(() => ({
    activeLanguage: inject(LanguageService).getLanguage(),
    availableLanguages: [],
    loading: false,
  })),
  withProps(() => {
    const transloco = inject(TranslocoService);
    return {
      _transloco: transloco,
      _language: inject(LanguageService),
      _loadedScopes: new Map<string, Set<string>>(),
      langChanges$: transloco.langChanges$,
    };
  }),
  withComputed(store => ({
    activeLanguageLabel: computed(() => {
      const language = store.activeLanguage();
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string translation must fall back
      return store._transloco.translate(`LANGUAGE.${language}`) || language.toUpperCase();
    }),
  })),
  withMethods(store => {
    /**
     * Checks the scope cache, calls fetchFn if needed, then merges the result.
     * Fire-and-forget: errors fall back silently (static JSON remains).
     */
    function loadScope(
      scope: string,
      fetchFn: (lang: string) => Observable<Translation>
    ): Observable<void> {
      if (isScopeLoaded(scope)) {
        return of(undefined);
      }
      const lang = store._transloco.getActiveLang();
      return fetchFn(lang).pipe(
        catchError(() => of({} as Translation)),
        tap(data => {
          if (data && Object.keys(data).length > 0) {
            mergeScope(scope, data);
          }
        }),
        map(() => undefined)
      );
    }

    /** Returns true if the API scope has already been merged for the active language. */
    function isScopeLoaded(scope: string): boolean {
      return store._loadedScopes.get(store._transloco.getActiveLang())?.has(scope) ?? false;
    }

    /**
     * Deep-merges raw API data into Transloco under the derived prefix key.
     * e.g. scope 'privacy-policy' → key 'PRIVACY_POLICY'
     */
    function mergeScope(scope: string, data: Translation): void {
      const lang = store._transloco.getActiveLang();
      const key = scope.toUpperCase().replace(/-/g, '_');
      store._transloco.setTranslation({ [key]: data }, lang, { merge: true });
      if (!store._loadedScopes.has(lang)) {
        store._loadedScopes.set(lang, new Set());
      }
      store._loadedScopes.get(lang)!.add(scope);
    }

    const loadScopeOnceRx = rxMethod<{
      scope: string;
      fetchFn: (lang: string) => Observable<Translation>;
    }>(pipe(mergeMap(({ scope, fetchFn }) => loadScope(scope, fetchFn))));

    return {
      initialize(languages: string[]): void {
        patchState(store, {
          availableLanguages: languages,
          activeLanguage: store._language.getLanguage(),
        });
      },
      setActiveLanguage(language: string): void {
        patchState(store, { loading: true });
        store._language.updateLanguage(language);
        store._transloco.setActiveLang(language);
        patchState(store, { activeLanguage: language, loading: false });
      },
      translate(key: string, params?: Record<string, unknown>): string {
        return store._transloco.translate(key, params);
      },
      selectTranslate(key: string, params?: Record<string, unknown>): Observable<string> {
        return store._transloco.selectTranslate(key, params);
      },
      translateObject<T>(keys: string[]): T {
        return store._transloco.translateObject(keys) as T;
      },
      getLanguageMenuItems(): MenuItem[] {
        return store.availableLanguages().map((lang: string) => ({
          label: `LANGUAGE.${lang.toUpperCase()}`,
          value: lang,
        }));
      },
      load(lang: string): Observable<Translation> {
        return store._transloco.load(lang);
      },
      getActiveLang(): string {
        return store._transloco.getActiveLang();
      },
      isScopeLoaded,
      mergeScope,
      clearScopeCache(): void {
        store._loadedScopes.clear();
      },
      loadScope,
      loadScopeOnce(scope: string, fetchFn: (lang: string) => Observable<Translation>): void {
        loadScopeOnceRx({ scope, fetchFn });
      },
    };
  }),
  withReset({ global: false })
);
